/**
 * AgentFlow — flow variable registry.
 *
 * Variables are interpolation tokens like `{businessName}` that appear
 * inside persona / greeting strings. The dialog's insert-variable picker
 * and the compiler's substitution pass both read from this registry.
 *
 * Adding a new variable is a one-line append here PLUS (when applicable)
 * a new substitution in the compiler / runtime. Until both are wired,
 * keep `available: false` so the picker shows it as "coming soon" and
 * authors don't ship templates that reference variables the runtime
 * doesn't yet resolve.
 *
 * Sources:
 *   - tenant       — static config (businessName today; address, hours, etc. later)
 *   - conversation — extracted during the conversation (customer name, phone)
 *   - runtime      — derived per turn (current date, time, day-of-week)
 *
 * Storage format never changes: `{key}` in the source string. The
 * registry is metadata only.
 */
export const FLOW_VARIABLES = [
    // ── Tenant settings ────────────────────────────────────────────────
    {
        key: "businessName",
        label: "Business name",
        description: "Your business's display name. Set in AgentFlow → Settings → Branding.",
        source: "tenant",
        available: true,
        sampleValue: "Acme HVAC",
    },
    // ── Conversation context (M3+ — not yet resolved at runtime) ───────
    {
        key: "customerName",
        label: "Customer name",
        description: "Extracted from the conversation when the customer introduces themselves.",
        source: "conversation",
        available: false,
        sampleValue: "Sarah",
    },
    {
        key: "customerPhone",
        label: "Customer phone",
        description: "The phone number the customer is texting from.",
        source: "conversation",
        available: false,
        sampleValue: "+1 (415) 555-0123",
    },
    {
        key: "lastBookingCode",
        label: "Last booking code",
        description: "Confirmation code from the most recent booking on this conversation.",
        source: "conversation",
        available: false,
        sampleValue: "BK-4F2A8C",
    },
    // ── Runtime (per-turn — not yet wired) ─────────────────────────────
    {
        key: "currentDate",
        label: "Current date",
        description: "Today's date in the tenant's timezone.",
        source: "runtime",
        available: false,
        sampleValue: "Friday, May 23",
    },
    {
        key: "currentTime",
        label: "Current time",
        description: "Current local time in the tenant's timezone.",
        source: "runtime",
        available: false,
        sampleValue: "10:55 AM",
    },
    {
        key: "currentDayOfWeek",
        label: "Day of week",
        description: "Current day name in the tenant's timezone.",
        source: "runtime",
        available: false,
        sampleValue: "Friday",
    },
];
export const FLOW_VARIABLES_BY_KEY = Object.fromEntries(FLOW_VARIABLES.map((v) => [v.key, v]));
export const AVAILABLE_VARIABLE_KEYS = new Set(FLOW_VARIABLES.filter((v) => v.available).map((v) => v.key));
export function tokenizeFlowTemplate(template) {
    const tokens = [];
    const re = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    let lastIdx = 0;
    let match;
    while ((match = re.exec(template)) !== null) {
        if (match.index > lastIdx) {
            tokens.push({ kind: "text", value: template.slice(lastIdx, match.index) });
        }
        const key = match[1] ?? "";
        tokens.push({
            kind: "variable",
            key,
            variable: FLOW_VARIABLES_BY_KEY[key] ?? null,
        });
        lastIdx = match.index + match[0].length;
    }
    if (lastIdx < template.length) {
        tokens.push({ kind: "text", value: template.slice(lastIdx) });
    }
    return tokens;
}
/**
 * Render-time interpolation helper. Substitutes only AVAILABLE
 * variables — anything else stays as a literal `{key}` so unknown or
 * not-yet-shipped braces don't get silently dropped. Used by the
 * dialog's live preview today; the compiler will adopt it once the
 * substitution surface grows past businessName.
 */
export function interpolateFlowVariables(template, values) {
    return template.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (match, key) => {
        if (AVAILABLE_VARIABLE_KEYS.has(key)) {
            const value = values[key];
            if (typeof value === "string")
                return value;
        }
        return match;
    });
}
//# sourceMappingURL=variables.js.map